import { initialize } from 'redux-form';
import { call, put, takeLatest } from 'redux-saga/effects';
import sortBy from 'lodash/sortBy';

import * as actNotif from '../../actions/notifications';
import { hideProgressBar, showProgressBar } from '../../actions';
import {
  EMPLOYEE_PIN_LIST_OUTLETS_EMPLOYEES_REQUEST,
  EMPLOYEE_PIN_ON_SUBMIT_FORM,
  EMPLOYEE_PIN_PAGE_REQUEST,
  EMPLOYEE_PIN_PIN_REQUEST,
  SUBMITTING_GET_OUTLETS_EMPLOYEES,
  SUBMITTING_GET_PERMISSIONS,
} from '../../actions/employees/pin/constants';
import {
  listOutletsEmployeesRequest,
  pinRequest,
  setAllowedFields,
  setOutletsEmployees,
  setSaveAppPermissionModal,
  setSubmitting,
  showSaveAppPermissionModal,
} from '../../actions/employees/pin';

import * as apiSetting from '../../api/permissions/settings';
import * as apiEmployees from '../../api/employees';
import { assertErrorHasMessage, assertErrorHasData } from '../../utils/assert';
import * as clevertapEmployeesPin from '../../clevertap/employees/pin';

const convertToPinFieldValues = (pinSettings) => {
  const fields = {};
  pinSettings.forEach((ps) => {
    fields[`permission-${ps.id}`] = ps.pin_required;
  });
  return fields;
};

const getPinRequestObject = (pinSettings) => {
  const fields: any[] = [];
  Object.keys(pinSettings).forEach((key) => {
    const data = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      permission_id: String(key).replace('permission-', ''),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      pin_required: pinSettings[key],
    };
    fields.push({
      ...data,
    });
  });
  return fields;
};

export function* listOutletsEmployeesRequestFlow() {
  try {
    yield put(showProgressBar());
    yield put(setSubmitting({ submitting: SUBMITTING_GET_OUTLETS_EMPLOYEES }));
    const response = yield call(apiEmployees.getOutletsEmployees);
    yield put(setOutletsEmployees({ outletsEmployees: response.data.outlets }));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(setSubmitting({ submitting: null }));
    yield put(hideProgressBar());
  }
}

/**
 * @param permissions current user permissions
 */
export function* managePermissions({ permissions }) {
  const allowedFields: any[] = [];
  // eslint-disable-next-line no-param-reassign
  permissions = sortBy(permissions, 'priority_rank');
  permissions.forEach((p) => {
    const data = {
      label: p.sub_menu_name,
      disabled: false,
      name: p.id,
      description: p.description,
      order: p.priorty_rank,
    };
    allowedFields.push({
      ...data,
    });
  });
  const sortedFields = sortBy(allowedFields, ['order']);
  yield put(setAllowedFields({ allowedFields: sortedFields }));
}

export function* pageRequestFlow(request) {
  const {
    payload: { user },
  } = request;

  yield put(pinRequest({ user }));

  yield call(listOutletsEmployeesRequestFlow, listOutletsEmployeesRequest());
}

export function* pinRequestFlow() {
  try {
    yield put(showProgressBar());
    yield put(setSubmitting({ submitting: SUBMITTING_GET_PERMISSIONS }));
    const responsePin = yield call(apiSetting.getPin, { params: {} });
    const values = convertToPinFieldValues(responsePin.data.pin_settings);
    yield call(managePermissions, {
      permissions: responsePin.data.pin_settings,
    });
    yield put(initialize('pin', { ...values }));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(setSubmitting({ submitting: null }));
    yield put(hideProgressBar());
  }
}

export function* onSubmitFormFlow(request) {
  const {
    payload: { values },
  } = request;

  let hasPinEnabled = false;
  const pinRequestObject = getPinRequestObject(values);
  Object.values(values).forEach((v) => {
    // @ts-expect-error ts-migrate(2322) FIXME: Type 'unknown' is not assignable to type 'boolean'... Remove this comment to see the full error message
    hasPinEnabled = hasPinEnabled || v;
  });
  try {
    yield put(showProgressBar());
    const response = yield call(apiSetting.putPin, {
      data: pinRequestObject,
      params: {},
    });
    yield put(initialize('pin', { ...values }));
    yield put(actNotif.success('Permission is successfully updated'));
    clevertapEmployeesPin.savePin({ pinSettings: response.data.pin_settings });
  } catch (error) {
    assertErrorHasData(error);
    if (error.data && error.data.error && error.data.error.code === 10) {
      yield put(
        setSaveAppPermissionModal({ message: error.data.error.user_message })
      );
      yield put(showSaveAppPermissionModal());
    } else {
      assertErrorHasMessage(error);
      yield put(actNotif.error(error.message));
    }
  } finally {
    yield put(hideProgressBar());
  }
}

export default function* saga() {
  yield takeLatest(EMPLOYEE_PIN_PAGE_REQUEST, pageRequestFlow);
  yield takeLatest(EMPLOYEE_PIN_ON_SUBMIT_FORM, onSubmitFormFlow);
  yield takeLatest(EMPLOYEE_PIN_PIN_REQUEST, pinRequestFlow);

  yield takeLatest(
    EMPLOYEE_PIN_LIST_OUTLETS_EMPLOYEES_REQUEST,
    listOutletsEmployeesRequestFlow
  );
}
